'use client';

import React from 'react';
import { ReloadIcon } from '@radix-ui/react-icons';
import { Button } from '@banzai/ui/components/button';
import { Input } from '@banzai/ui/components/input';
import { Label } from '@banzai/ui/components/label';
import { validateEmail } from '@banzai/utils/email';
import { wait } from '@banzai/utils/promises';
import { useState } from 'react';
import { FaCircleCheck } from 'react-icons/fa6';
import { IconContext } from 'react-icons';
import { fetchClient } from '#client';
import { logger } from '@banzai/utils/logging';
import { useTheme } from '@banzai/themes/use-theme';

export function PasswordReset() {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const actualEmail = email.toLowerCase().trim();
  const emailValid = validateEmail(actualEmail);

  const theme = useTheme();

  function handleEmailChange(e: any) {
    setEmail(e.target.value);
  }

  async function submitPasswordRecover(e: any) {
    e.preventDefault();
    setIsSubmitting(true);
    await wait(1000);

    try {
      await fetchClient.password.createResetRequest.mutate({
        email: actualEmail,
        service_id: theme.theme.id,
      });
      setHasSubmitted(true);
    } catch (e) {
      logger().error(e, `Could not create password reset request.`);
      setHasError(true);
      setIsSubmitting(false);
      return;
    }
  }

  return (
    (!hasSubmitted && (
      <div className="space-y-6">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight font-logo">Password Reset</h1>
          <p className="text-sm text-muted-foreground">
            Forgot your password? No problem. We will get it sorted out.
          </p>
        </div>
        <div>
          <form onSubmit={submitPasswordRecover} className={`grid gap-2 container resize-none p-1`}>
            <div>
              <Label className="sr-only" htmlFor="email">
                Email
              </Label>
              <Input
                id="email"
                placeholder="name@example.com"
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                value={email as any}
                disabled={isSubmitting || hasSubmitted}
                onChange={handleEmailChange}
              />
            </div>
            {hasError && (
              <div className="text-destructive text-sm p-2 text-center">Something went wrong.</div>
            )}
            <Button disabled={!emailValid || isSubmitting || hasSubmitted}>
              {isSubmitting && <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />}
              Reset Password
            </Button>
          </form>
        </div>
      </div>
    )) || (
      <div className="space-y-6">
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 text-primary animate-jump-in animate-once">
            <IconContext.Provider value={{ size: '100%' }}>
              <FaCircleCheck />
            </IconContext.Provider>
          </div>
        </div>
        <div className="text-sm text-center">
          We have received your password recovery request. If the email address you entered is
          associated with an account you will receive an email shortly.
        </div>
      </div>
    )
  );
}
